import { useState } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { useTheme } from '../contexts/ThemeContext'
import './Layout.css'

export function Layout() {
  const { theme, toggleTheme } = useTheme()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const isActive = (path: string) => location.pathname === path

  const closeMobileMenu = () => setMobileMenuOpen(false)

  return (
    <div className="layout">
      <div className="color-orb color-orb-1" />
      <div className="color-orb color-orb-2" />
      <div className="color-orb color-orb-3" />

      {/* Mobile Navigation */}
      <div className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-nav-header">
          <Link to="/" className="logo" onClick={closeMobileMenu}>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <circle cx="8" cy="8" r="6" fill="#ef4444" />
              <circle cx="24" cy="8" r="6" fill="#22c55e" />
              <circle cx="8" cy="24" r="6" fill="#3b82f6" />
              <circle cx="24" cy="24" r="6" fill="#eab308" />
            </svg>
            <span>ColorKit</span>
          </Link>
          <button className="mobile-nav-close" onClick={closeMobileMenu} aria-label="Close menu">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <nav className="mobile-nav-links">
          <Link to="/" className={`mobile-nav-link ${isActive('/') ? 'active' : ''}`} onClick={closeMobileMenu}>Home</Link>
          <Link to="/api" className={`mobile-nav-link ${isActive('/api') ? 'active' : ''}`} onClick={closeMobileMenu}>API</Link>
          <Link to="/docs" className={`mobile-nav-link ${isActive('/docs') ? 'active' : ''}`} onClick={closeMobileMenu}>Docs</Link>
          <Link to="/examples" className={`mobile-nav-link ${isActive('/examples') ? 'active' : ''}`} onClick={closeMobileMenu}>Examples</Link>
          <Link to="/playground" className={`mobile-nav-link ${isActive('/playground') ? 'active' : ''}`} onClick={closeMobileMenu}>Playground</Link>
        </nav>
      </div>

      <header className="header">
        <div className="header-container">
          <Link to="/" className="logo">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <circle cx="8" cy="8" r="6" fill="#ef4444" />
              <circle cx="24" cy="8" r="6" fill="#22c55e" />
              <circle cx="8" cy="24" r="6" fill="#3b82f6" />
              <circle cx="24" cy="24" r="6" fill="#eab308" />
            </svg>
            <span>ColorKit</span>
          </Link>

          <nav className="nav">
            <Link to="/" className={`nav-link ${isActive('/') ? 'active' : ''}`}>Home</Link>
            <Link to="/api" className={`nav-link ${isActive('/api') ? 'active' : ''}`}>API</Link>
            <Link to="/docs" className={`nav-link ${isActive('/docs') ? 'active' : ''}`}>Docs</Link>
            <Link to="/examples" className={`nav-link ${isActive('/examples') ? 'active' : ''}`}>Examples</Link>
            <Link to="/playground" className={`nav-link ${isActive('/playground') ? 'active' : ''}`}>Playground</Link>
          </nav>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
              {theme === 'light' ? (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"/>
                </svg>
              )}
            </button>
            <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      <main className="main">
        <Outlet />
      </main>

      <footer className="footer">
        <div className="footer-container">
          <div className="footer-left">
            <p className="footer-copyright">&copy; 2025 ColorKit. MIT License.</p>
          </div>
          <div className="footer-center">
            <p className="footer-creator">
              Created by{' '}
              <a href="https://github.com/ersinkoc" target="_blank" rel="noopener noreferrer">
                Ersin KOC
              </a>
            </p>
          </div>
          <div className="footer-right">
            <a
              href="https://github.com/ersinkoc/colorkit"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-github"
              aria-label="View on GitHub"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
              <span>GitHub</span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  )
}
